import React, { useEffect, useRef, useState } from "react";
import { maintenanceApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Button, Loading, ErrorState, PageHeader, Badge } from "../components/ui";
import { Icon } from "../components/icons";
import { useAuth, hasRole } from "../../context/AuthContext";

/** Format ukuran byte → KB/MB terbaca. */
function fmtBytes(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

/** Warna tag level di viewer (tema gelap console). */
const LEVEL_STYLE = {
  INFO: "text-emerald-400",
  WARN: "text-amber-400",
  ERROR: "text-rose-400 font-semibold",
  DEBUG: "text-slate-400",
  HTTP: "text-cyan-400",
};

/** Warnai status HTTP menurut kelasnya (2xx hijau, 3xx cyan, 4xx kuning, 5xx merah). */
function HttpBody({ msg }) {
  return (
    <span className="text-slate-200">
      {msg.split(" ").map((p, i) => {
        if (/^\d{3}$/.test(p)) {
          const s = Number(p);
          const c =
            s >= 500 ? "text-rose-400" : s >= 400 ? "text-amber-400" : s >= 300 ? "text-cyan-400" : "text-emerald-400";
          return (
            <span key={i} className={`${c} font-semibold`}>
              {p}{" "}
            </span>
          );
        }
        return <span key={i}>{p} </span>;
      })}
    </span>
  );
}

/** Render satu baris log: [waktu] [LEVEL] pesan, dengan pewarnaan. */
function LogLine({ raw }) {
  const m = raw.match(/^\[([^\]]+)\]\s\[([A-Z]+)\s*\]\s?([\s\S]*)$/);
  if (!m) return <div className="whitespace-pre-wrap break-all text-slate-300">{raw}</div>;
  const [, time, level, msg] = m;
  const cls = LEVEL_STYLE[level] || "text-slate-300";
  return (
    <div className="whitespace-pre-wrap break-all leading-5">
      <span className="text-slate-500">[{time}]</span>{" "}
      <span className={cls}>[{level.padEnd(5)}]</span>{" "}
      {level === "HTTP" ? <HttpBody msg={msg} /> : <span className="text-slate-200">{msg}</span>}
    </div>
  );
}

/**
 * MAINTENANCE / PANTAU SERVER.
 * - Viewer log LIVE (isi file.log) dengan auto-refresh + pewarnaan level.
 * - Perintah tail per-OS (Ubuntu / Windows) untuk pantau di terminal.
 * - Daftar arsip log harian + tombol hapus arsip < hari ini (SUPER_ADMIN).
 */
export default function MaintenancePage() {
  const toast = useAdminToast();
  const { user } = useAuth();
  const canCleanup = hasRole(user, "SUPER_ADMIN");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Viewer log live
  const [lines, setLines] = useState([]);
  const [lineCount, setLineCount] = useState(200);
  const [auto, setAuto] = useState(true);
  const [logErr, setLogErr] = useState(null);
  const consoleRef = useRef(null);
  const stickBottom = useRef(true);
  const offsetRef = useRef(undefined); // byte offset terakhir → fetch incremental
  const MAX_LINES = 2000; // batasi memori viewer

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await maintenanceApi.logsStatus());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // full=true → muatan awal (ambil N baris terakhir, ganti isi).
  // full=false → incremental: hanya baris baru sejak offset terakhir (hemat).
  const loadLogs = async (full = false) => {
    try {
      const after = full ? undefined : offsetRef.current;
      const res = await maintenanceApi.tailLogs({ lines: lineCount, after });
      offsetRef.current = res.size;
      const incoming = res.lines || [];
      if (res.reset) {
        setLines(incoming); // muatan awal / rotasi → ganti total
      } else if (incoming.length) {
        setLines((prev) => [...prev, ...incoming].slice(-MAX_LINES)); // tambah baris baru
      }
      setLogErr(null);
    } catch (e) {
      setLogErr(errorMessage(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Muat ulang penuh saat jumlah baris berubah (reset offset).
  useEffect(() => {
    offsetRef.current = undefined;
    loadLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineCount]);

  // Auto-refresh tiap 3 detik (incremental). Dijeda otomatis saat tab tak aktif.
  useEffect(() => {
    if (!auto) return;
    const tick = () => {
      if (!document.hidden) loadLogs(false);
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, lineCount]);

  // Auto-scroll ke bawah bila pengguna sedang menempel di dasar.
  useEffect(() => {
    const el = consoleRef.current;
    if (el && stickBottom.current) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const onConsoleScroll = () => {
    const el = consoleRef.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const cleanup = async () => {
    if (!canCleanup) return;
    const n = data?.archives?.length || 0;
    if (n === 0) {
      toast.info("Tidak ada arsip log lama untuk dihapus");
      return;
    }
    if (!window.confirm(`Hapus ${n} arsip log lama (sebelum hari ini)? File log hari ini tidak terpengaruh.`)) {
      return;
    }
    setBusy(true);
    try {
      const res = await maintenanceApi.cleanupLogs();
      toast.success(
        res.deleted?.length
          ? `${res.deleted.length} arsip log dihapus (${fmtBytes(res.freedBytes)})`
          : "Tidak ada arsip log lama untuk dihapus"
      );
      if (res.failed?.length) toast.error(`Gagal menghapus: ${res.failed.join(", ")}`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const archives = data?.archives || [];
  const totalBytes = data?.totalBytes || 0;
  const logPath = data?.currentLog || "backend/log/file.log";

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Maintenance"
        subtitle="Pantau & bersihkan file log server"
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={load} disabled={loading || busy}>
              <Icon.Refresh size={16} /> Muat ulang
            </Button>
            {canCleanup && (
              <Button variant="danger" onClick={cleanup} disabled={busy || loading || archives.length === 0}>
                <Icon.Trash size={16} /> {busy ? "Menghapus…" : "Hapus log lama"}
              </Button>
            )}
          </div>
        }
      />

      {loading ? (
        <Loading label="Memuat status log…" />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <div className="flex-1 min-h-0 overflow-auto space-y-4">
          {/* Viewer log LIVE */}
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2 text-slate-700">
                <Icon.Server size={18} className="text-brand-600" />
                <h2 className="font-bold">Log Live</h2>
                <Badge color={auto ? "green" : "gray"}>{auto ? "● auto 3s" : "jeda"}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={lineCount}
                  onChange={(e) => setLineCount(Number(e.target.value))}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  {[100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      {n} baris
                    </option>
                  ))}
                </select>
                <Button variant="secondary" size="sm" onClick={() => loadLogs(true)}>
                  <Icon.Refresh size={14} /> Refresh
                </Button>
                <Button variant={auto ? "success" : "secondary"} size="sm" onClick={() => setAuto((v) => !v)}>
                  {auto ? "Jeda" : "Mulai auto"}
                </Button>
              </div>
            </div>

            {logErr ? (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{logErr}</div>
            ) : (
              <div
                ref={consoleRef}
                onScroll={onConsoleScroll}
                className="bg-slate-900 rounded-lg p-3 font-mono text-[12px] text-slate-200 h-80 overflow-auto"
              >
                {lines.length === 0 ? (
                  <div className="text-slate-500">Belum ada baris log.</div>
                ) : (
                  lines.map((l, i) => <LogLine key={i} raw={l} />)
                )}
              </div>
            )}
            <p className="text-[12px] text-slate-400 mt-2">
              Menampilkan {lines.length} baris terakhir dari <code>{logPath}</code>. Auto-refresh tiap 3 detik.
            </p>
          </Card>

          {/* Perintah pantau di terminal (per-OS) */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3 text-slate-700">
              <Icon.File size={18} className="text-brand-600" />
              <h2 className="font-bold">Pantau via Terminal</h2>
            </div>
            <p className="text-sm text-slate-500 mb-2">Server cloud (Ubuntu / Linux / Mac):</p>
            <div className="bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-[12.5px] font-mono overflow-x-auto">
              tail -F {logPath}
            </div>
            <p className="text-sm text-slate-500 mt-3 mb-2">Dev lokal (Windows PowerShell):</p>
            <div className="bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-[12.5px] font-mono overflow-x-auto">
              Get-Content -Path "{logPath}" -Wait -Tail 50
            </div>
            <p className="text-[12px] text-slate-400 mt-2">
              Rotasi otomatis harian: saat pergantian hari, <code>file.log</code> diarsipkan menjadi{" "}
              <code>file-YYYY-MM-DD.log</code> dan file baru dimulai untuk hari ini.
            </p>
          </Card>

          {/* Arsip log lama */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-700">
                <Icon.File size={18} className="text-brand-600" />
                <h2 className="font-bold">Arsip Log Lama</h2>
                <Badge color={archives.length ? "yellow" : "green"}>
                  {archives.length} file · {fmtBytes(totalBytes)}
                </Badge>
              </div>
            </div>

            {archives.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Bersih — tidak ada arsip log lama.</div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-3">
                  Semua arsip di bawah bertanggal <b>sebelum hari ini</b>. Penghapusan bersifat <b>manual</b> (tidak
                  otomatis)
                  {canCleanup
                    ? " — tekan tombol “Hapus log lama” di atas."
                    : " dan hanya dapat dilakukan oleh SUPER_ADMIN."}
                </p>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {archives.map((f) => (
                    <div key={f.name} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50">
                      <span className="font-mono text-slate-700 truncate">{f.name}</span>
                      <span className="text-slate-400 text-[12px] shrink-0 ml-3">{fmtBytes(f.size)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
